import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast as message } from 'sonner';
import EnhancedDialog from '../EnhancedDialog';
import { RecipientTo } from '../SendOffer/RecipientEditor';
import { invalidateAndRefreshAllQueriesForTable } from '../../utils/metadata';
import { BLOCKED_REASON, previewAppout, sendAppout, type AppoutPreview } from './appout';
import '../SendOffer/SendOfferDialog.css';

/**
 * App Out dialog (REG-918): the Hilton App Out the rep is about to send, as
 * the server renders it, from the rep's own @hiltoncaps.com mailbox. The rep
 * may pick the merchant address on file, adjust the greeting name and the
 * subject; every edit re-renders the preview on the server, and Send sends
 * exactly that (public.appout_send re-checks everything).
 */
export function AppOutDialog({ merchantId, label, onClose, onSent }: {
  merchantId: number | null;
  label?: string | null;
  onClose: () => void;
  onSent?: () => void;
}): React.ReactElement | null {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<AppoutPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toEmail, setToEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const request = useRef(0);
  const isOpen = merchantId != null;

  const load = async (next: { toEmail?: string; firstName?: string | null; subject?: string }) => {
    if (merchantId == null) return;
    const id = ++request.current;
    setLoading(true);
    try {
      const result = await previewAppout({ merchantId, ...next });
      if (id !== request.current) return;
      setPreview(result);
      setLoadError(null);
      setToEmail(result.to ?? '');
      setFirstName(result.first_name ?? '');
      setSubject(result.subject);
    } catch (err) {
      if (id !== request.current) return;
      setLoadError(err instanceof Error ? err.message : 'Could not load the App Out.');
    } finally {
      if (id === request.current) setLoading(false);
    }
  };

  // Fresh preview each time the dialog opens for a merchant.
  useEffect(() => {
    setPreview(null);
    setLoadError(null);
    setSending(false);
    if (merchantId != null) void load({ firstName: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  if (!isOpen) return null;

  const dirty = preview != null && (
    toEmail !== (preview.to ?? '') || firstName !== (preview.first_name ?? '') || subject !== preview.subject);
  const refreshPreview = () => load({ toEmail, firstName, subject });
  const blocked = preview?.blocked ?? null;
  const canSend = Boolean(preview && !blocked && !dirty && !loading && !sending && toEmail && subject.trim());

  const handleSend = async () => {
    if (!preview || merchantId == null || !canSend) return;
    setSending(true);
    try {
      const result = await sendAppout({ merchantId, toEmail, firstName, subject });
      message.success(result.sandbox
        ? `Sandbox: App Out sent to ${result.delivered_to} (intended for ${result.to}) from ${preview.from_email}`
        : `App Out sent to ${result.to} from ${preview.from_email}`);
      await Promise.all([
        invalidateAndRefreshAllQueriesForTable('merchant'),
        invalidateAndRefreshAllQueriesForTable('merchant_call_log'),
        queryClient.invalidateQueries({ queryKey: ['opportunity-all'] }),
        queryClient.invalidateQueries({ queryKey: ['opportunity-workspace-meta'] }),
      ]);
      onSent?.();
      onClose();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Could not send the App Out');
      setSending(false);
    }
  };

  const title = `Send App Out${label || preview?.business_name ? `: ${label || preview?.business_name}` : ''}`;
  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={sending ? () => {} : onClose}
      title={title}
      className="send-offer-dialog appout-dialog"
      canOutsideClickClose={false}
      canEscapeKeyClose={!sending}
    >
      <div className="sky-dialog-body" data-testid="appout-dialog">
        {loadError && (
          <div className="sky-callout sky-callout-danger" role="alert" style={{ marginBottom: 12 }}>{loadError}</div>
        )}
        {!preview && !loadError && <div className="sky-spinner" aria-label="Loading the App Out" />}
        {preview && (
          <>
            {preview.sandbox && (
              <div className="sky-callout sky-callout-warning" data-testid="appout-sandbox" style={{ marginBottom: 12 }}>
                Sandbox: this App Out goes only to <b>{preview.delivered_to}</b>, not to the merchant.
              </div>
            )}
            {blocked && (
              <div className="sky-callout sky-callout-danger" data-testid="appout-blocked" role="alert" style={{ marginBottom: 12 }}>
                {BLOCKED_REASON[blocked]}
              </div>
            )}
            <div className="send-offer-envelope">
              <div><span className="send-offer-envelope-label">From</span> {preview.from_name} &lt;{preview.from_email}&gt;</div>
              <RecipientTo options={preview.to_options} value={toEmail} onChange={setToEmail} disabled={sending} />
              <div className="send-offer-recipients">
                <span className="send-offer-envelope-label">Name</span>
                <input
                  className="send-offer-subject-input"
                  aria-label="Greeting name"
                  placeholder="First name (blank: just “Hi”)"
                  value={firstName}
                  maxLength={40}
                  disabled={sending}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="send-offer-recipients">
                <span className="send-offer-envelope-label">Subject</span>
                <input
                  className="send-offer-subject-input"
                  aria-label="Email subject"
                  value={subject}
                  maxLength={200}
                  disabled={sending}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              {dirty && (
                <div className="send-offer-recipients">
                  <button type="button" className="sky-btn sky-btn-ghost" onClick={refreshPreview} disabled={loading || sending}>
                    Update preview
                  </button>
                  <small className="send-offer-recipients-hint">Update the preview to send what you changed.</small>
                </div>
              )}
            </div>
            <iframe className="send-offer-preview" title="App Out email preview" sandbox="" srcDoc={preview.html} />
          </>
        )}
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={onClose} disabled={sending}>Cancel</button>
        <button className="sky-btn sky-btn-primary" onClick={handleSend} disabled={!canSend} aria-busy={sending}>
          {sending ? <span className="sky-spinner" /> : null}
          {sending ? 'Sending…' : 'Send App Out'}
        </button>
      </div>
    </EnhancedDialog>
  );
}

export default AppOutDialog;
